import { IStairConfig, IStepGeometry, IStringerGeometry } from '@stair-craft/common/util';

import { STRINGER_WIDTH } from '../../constants';
import { RESIDENTIAL_NORMS } from '../../norms';
import { boardBand } from '../shared-board-band/board-band';
import { kosourProfile, kosourRunAxis } from '../shared-board-profile/board-profile';
import { IPlanPoint, planCentroid } from '../shared-plan-geometry/plan-geometry';
import { tetivaDrop, tetivaLift } from '../shared-tetiva-board/tetiva-board';
import { IRiserStop, winderRiserOutline } from '../shared-winder-riser-outline/winder-riser-outline';
import { ITurnSupportStep, buildTurnSupports, lineThrough } from './build-turn-supports';

type IWinderCorners = NonNullable<IStepGeometry['winderCorners']>;

function outerEdge(corners: IWinderCorners): IPlanPoint[] {
    return [corners.outerBack, ...(corners.outerMidPoints ?? []), corners.outerFront];
}

function innerEdge(corners: IWinderCorners): IPlanPoint[] {
    return [corners.innerBack, ...(corners.innerMidPoints ?? []), corners.innerFront];
}

function interiorOf(corners: IWinderCorners): IPlanPoint {
    return planCentroid([corners.innerBack, corners.innerFront, corners.outerBack, corners.outerFront]);
}

/**
 * Winders as the boards under them see them: the outer wall always carries a board, the inner
 * edge only where the turn has an inner wall (a U turn) — an L turn's inner corner is open.
 */
export function winderSupportSteps(steps: readonly IStepGeometry[], withInnerWall: boolean): ITurnSupportStep[] {
    return steps.flatMap((step: IStepGeometry): ITurnSupportStep[] => {
        const corners: IWinderCorners | undefined = step.winderCorners;
        if (!corners) {
            return [];
        }
        return [
            {
                top: step.position.y,
                lead: lineThrough(corners.innerBack, corners.outerBack),
                trail: lineThrough(corners.innerFront, corners.outerFront),
                walls: withInnerWall ? [outerEdge(corners), innerEdge(corners)] : [outerEdge(corners)],
                interior: interiorOf(corners),
            },
        ];
    });
}

/** Cuts every winder riser where the side boards take over (kosour) or at the wall line (тетива). */
export function applyWinderRiserOutlines(steps: readonly IStepGeometry[], withInnerWall: boolean, config: IStairConfig): void {
    const riserEnd: number = boardBand(config.stringerType).riserEnd;
    for (const step of steps) {
        const corners: IWinderCorners | undefined = step.winderCorners;
        if (!corners) {
            continue;
        }
        const interior: IPlanPoint = interiorOf(corners);
        const edges: IPlanPoint[][] = withInnerWall ? [outerEdge(corners), innerEdge(corners)] : [outerEdge(corners)];
        const stops: IRiserStop[] = edges
            .filter((edge: IPlanPoint[]) => edge.length >= 2)
            .map((edge: IPlanPoint[]) => ({ from: edge[0], to: edge[1], inward: interior, distance: riserEnd }));
        step.riserOutline = winderRiserOutline(corners, stops);
    }
}

/**
 * The lowest bottom of the flight-1 kosours where they end at the turn, or `undefined` without
 * them. Flight-1 boards are the kosours that start below the first turn step.
 */
export function flightEntryBottom(stringers: readonly IStringerGeometry[], turnSteps: readonly IStepGeometry[]): number | undefined {
    const turnTop: number | undefined = turnSteps.at(0)?.position.y;
    const ends: number[] = stringers
        .filter((stringer: IStringerGeometry) => !stringer.block && stringer.bottomSlope && turnTop !== undefined)
        .filter((stringer: IStringerGeometry) => (stringer.points.at(0)?.y ?? Infinity) < (turnTop ?? -Infinity))
        .flatMap((stringer: IStringerGeometry) => {
            const endRun: number = kosourRunAxis(stringer.points).runOf(stringer.points[stringer.points.length - 1]);
            return kosourProfile(stringer.points, stringer.width, stringer.bottomSlope, stringer.runSpan)
                .filter((point: { run: number; y: number }) => Math.abs(point.run - endRun) < 1)
                .map((point: { run: number; y: number }) => point.y);
        });
    return ends.length > 0 ? Math.min(...ends) : undefined;
}

/** Board heights of the turn zone, matched to the flight boards on both sides. */
export function turnSupportOptions(
    config: IStairConfig,
    riserHeight: number,
    treadDepth: number,
    entryBottom?: number
): Parameters<typeof buildTurnSupports>[1] {
    return {
        entryBottom,
        riserHeight,
        stringerType: config.stringerType,
        treadThickness: config.treadThickness,
        kosourDrop: STRINGER_WIDTH / Math.cos(Math.atan2(riserHeight, RESIDENTIAL_NORMS.treadDepthIdeal)),
        tetivaLift: tetivaLift(riserHeight, treadDepth, config.noseLength),
        tetivaDrop: tetivaDrop(riserHeight, treadDepth),
    };
}

/** Boards under a rectangular landing: one wall polyline from the flight-1 edge to the flight-2 edge. */
export function landingSupports(
    landing: IStepGeometry,
    wall: IPlanPoint[],
    exitLine: [IPlanPoint, IPlanPoint],
    options: Parameters<typeof buildTurnSupports>[1]
): IStringerGeometry[] {
    const x0: number = landing.position.x;
    const z0: number = landing.position.z;
    const step: ITurnSupportStep = {
        walls: [wall],
        top: landing.position.y,
        lead: lineThrough({ x: x0, z: z0 }, { x: x0 + landing.treadWidth, z: z0 }),
        trail: lineThrough(exitLine[0], exitLine[1]),
        interior: { x: x0 + landing.treadWidth / 2, z: z0 + landing.treadDepth / 2 },
    };
    return buildTurnSupports([step], { ...options, level: true });
}
